import type {
  DashboardPageData,
  DashboardRunOption,
  DashboardScenario,
} from "./dashboard.types.ts";

export const dashboardRunOptions: DashboardRunOption[] = [
  { id: "completed-run", label: "Redistribution Run: 2025-05-20 09:30" },
  { id: "processing-run", label: "Redistribution Run: 2025-05-21 07:15" },
  { id: "empty-run", label: "Redistribution Run: 2025-05-19 14:00" },
  { id: "error-run", label: "Redistribution Run: 2025-05-18 18:40" },
];

const loadedDashboardData: DashboardPageData = {
  pageTitle: "Insight.IQ",
  pageSubtitle: "PO Redistribution Control Tower",
  lastRunLabel: "May 20, 2025 9:41 AM",
  filterSummary: "Showing all default landing page sections",
  summaryStats: [
    {
      id: "recommended-movements",
      label: "Recommended Movements",
      value: "24",
      detail: "Total inventory movements",
      accentColor: "#3b82f6",
      icon: "↻",
    },
    {
      id: "critical-stockouts",
      label: "Critical Stockouts Prevented",
      value: "6",
      detail: "High risk stockouts avoided",
      accentColor: "#ef4444",
      icon: "!",
    },
    {
      id: "moderate-risks",
      label: "Moderate Risks Prevented",
      value: "15",
      detail: "Moderate risk stockouts mitigated",
      accentColor: "#f59e0b",
      icon: "▲",
    },
    {
      id: "locations-protected",
      label: "Locations Protected",
      value: "4",
      detail: "DCs protected from stockouts",
      accentColor: "#84cc16",
      icon: "⌂",
    },
    {
      id: "units-reallocated",
      label: "Units Reallocated",
      value: "482",
      detail: "Total units reallocated",
      accentColor: "#38bdf8",
      icon: "◫",
    },
    {
      id: "wos-variance",
      label: "WOS Variance Reduction",
      value: "32%",
      detail: "Improvement in WOS variance",
      accentColor: "#a855f7",
      icon: "↗",
    },
  ],
  reasonBreakdown: [
    { label: "Critical Stockout Prevention", value: 6, color: "#ef4444" },
    { label: "Moderate Stockout Prevention", value: 15, color: "#f59e0b" },
    { label: "Inventory Balancing", value: 3, color: "#3b82f6" },
  ],
  inventoryHealthBefore: [
    { label: "Critical", value: 6, colorClass: "dashboard-pill--critical" },
    { label: "Moderate", value: 15, colorClass: "dashboard-pill--moderate" },
    { label: "Healthy", value: 8, colorClass: "dashboard-pill--healthy" },
  ],
  inventoryHealthAfter: [
    { label: "Critical", value: 0, colorClass: "dashboard-pill--critical" },
    { label: "Moderate", value: 2, colorClass: "dashboard-pill--moderate" },
    { label: "Healthy", value: 27, colorClass: "dashboard-pill--healthy" },
  ],
  inventoryHealthSummary:
    "Overall inventory health improved — high & moderate risk locations reduced from 21 to 2 (-90.5%).",
  totalLocations: 29,
  businessImpact: [
    { label: "Purchase Orders Optimized", value: "21" },
    { label: "SKUs Rebalanced", value: "37" },
    {
      label: "Average WOS Improvement",
      value: "+1.4 weeks",
      accent: "positive",
    },
    {
      label: "Estimated Transfer Avoidance",
      value: "$18,450",
      accent: "positive",
    },
    {
      label: "Service Level Improvement",
      value: "+6.2%",
      accent: "positive",
    },
  ],
  topInventoryWins: [
    {
      sku: "192038-39",
      before: "Critical",
      beforeTone: "critical",
      after: "Healthy",
      afterTone: "healthy",
      improvement: "+2.1 weeks",
      outcome: "Stockout Avoided",
    },
    {
      sku: "209384-16",
      before: "Moderate",
      beforeTone: "moderate",
      after: "Healthy",
      afterTone: "healthy",
      improvement: "+1.7 weeks",
      outcome: "Risk Reduced",
    },
    {
      sku: "109384-21",
      before: "Overstocked",
      beforeTone: "overstocked",
      after: "Balanced",
      afterTone: "balanced",
      improvement: "-1.3 weeks",
      outcome: "Inventory Optimized",
    },
    {
      sku: "308472-00",
      before: "Moderate",
      beforeTone: "moderate",
      after: "Healthy",
      afterTone: "healthy",
      improvement: "+1.6 weeks",
      outcome: "Risk Reduced",
    },
    {
      sku: "408291-14",
      before: "Critical",
      beforeTone: "critical",
      after: "Healthy",
      afterTone: "healthy",
      improvement: "+2.4 weeks",
      outcome: "Stockout Avoided",
    },
  ],
  redistributionActivity: [
    {
      sku: "192038-39",
      fromPurchaseOrder: "PO-10012",
      fromDistributionCenter: "DC-07",
      toPurchaseOrder: "PO-10044",
      toDistributionCenter: "DC-02",
      unitsMoved: 12,
      reason: "Critical Stockout Prevention",
      reasonTone: "critical",
    },
    {
      sku: "209384-16",
      fromPurchaseOrder: "PO-10055",
      fromDistributionCenter: "DC-09",
      toPurchaseOrder: "PO-10061",
      toDistributionCenter: "DC-01",
      unitsMoved: 6,
      reason: "Moderate Stockout Prevention",
      reasonTone: "moderate",
    },
    {
      sku: "109384-21",
      fromPurchaseOrder: "PO-10012",
      fromDistributionCenter: "DC-07",
      toPurchaseOrder: "PO-10073",
      toDistributionCenter: "DC-03",
      unitsMoved: 8,
      reason: "Inventory Balancing",
      reasonTone: "balanced",
    },
    {
      sku: "308472-00",
      fromPurchaseOrder: "PO-10088",
      fromDistributionCenter: "DC-10",
      toPurchaseOrder: "PO-10044",
      toDistributionCenter: "DC-02",
      unitsMoved: 10,
      reason: "Moderate Stockout Prevention",
      reasonTone: "moderate",
    },
    {
      sku: "408291-14",
      fromPurchaseOrder: "PO-10055",
      fromDistributionCenter: "DC-09",
      toPurchaseOrder: "PO-10061",
      toDistributionCenter: "DC-01",
      unitsMoved: 7,
      reason: "Critical Stockout Prevention",
      reasonTone: "critical",
    },
  ],
  activityPaginationLabel: "Showing 1-5 of 24",
  footerSummary: {
    confidenceScore: "92%",
    confidenceLabel: "High confidence in recommendations",
    runStatus: "Run completed successfully",
    runCompletedAt: "Completed on May 20, 2025 9:30 AM",
    runDuration: "Duration: 00:01:24",
    traceLogLabel: "View Algorithm Trace Log",
  },
};

export const dashboardScenarios: Record<string, DashboardScenario> = {
  "completed-run": {
    status: "loaded",
    pageData: loadedDashboardData,
  },
  "processing-run": {
    status: "loading",
    stateTitle: "Refreshing the redistribution control tower",
    stateMessage:
      "We're compiling the latest run metadata, KPI cards, and movement snapshots.",
  },
  "empty-run": {
    status: "empty",
    stateTitle: "No completed redistribution activity for this run",
    stateMessage:
      "Select another run or launch PO Recommendations to generate a new recommendation set.",
  },
  "error-run": {
    status: "error",
    stateTitle: "Dashboard metrics are temporarily unavailable",
    stateMessage:
      "We couldn't assemble the control tower metrics for this run. Review the Algorithm Trace Log or switch to a prior completed run.",
  },
};
